import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Checkbox,
  Card,
  CardContent,
  IconButton,
  Snackbar,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
  Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../context/AuthContext';
import { groupPlayAPI, amenityAPI, amenityScheduleAPI, membershipTierAPI, tenantSettingsAPI, userAPI } from '../../services/api';
import {
  GroupPlay,
  RecurringPattern,
  BookingWindowConfiguration,
  MembershipTier,
  TierAccessLevel,
  TierAccessConfiguration,
  TenantSettings,
  UserRole,
} from '../../types';
import { formatDateForInput, parseDateOnlyString, formatDate, formatTimeString } from '../../utils/dateUtils';
import { validateCoachingTimeSlotAlignment, formatSlotAlignmentMessage, dateToTimeString } from '../../utils/slotValidation';
import AdminAccessControl from '../common/AdminAccessControl';
import ReservationResultsDialog from '../coaching/ReservationResultsDialog';

const CreateEditGroupPlayPage: React.FC = () => {
  const { groupPlayId } = useParams<{ groupPlayId: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copyFrom');
  const navigate = useNavigate();
  const { user } = useAuth();

  const isEditing = Boolean(groupPlayId);
  const isCopying = Boolean(copyFromId);

  const [groupPlayData, setGroupPlayData] = useState<GroupPlay | null>(null);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [coaches, setCoaches] = useState<any[]>([]);
  const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);
  const [affectedInstancesDialogOpen, setAffectedInstancesDialogOpen] = useState(false);
  const [affectedInstancesData, setAffectedInstancesData] = useState<any>(null);
  const [pendingEndDate, setPendingEndDate] = useState<Date | null>(null);
  const [isCheckingAffectedInstances, setIsCheckingAffectedInstances] = useState(false);

  const DEFAULT_DURATION = 60; // minutes

  // Form state
  const [formData, setFormData] = useState(() => {
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + DEFAULT_DURATION);

    return {
      name: '',
      type: '',
      description: '',
      coachId: '',
      isGuestCoach: false,
      guestCoachName: '',
      venues: [] as string[],
      scheduleDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      durationMinutes: DEFAULT_DURATION,
      capacity: 12,
      isWaitlistAvailable: true,
      allowGuestBookings: true,
      memberSessionFee: 0,
      guestSessionFee: 0,
      active: true,
      notes: '',
      recurringPattern: null as RecurringPattern | null,
      isRecurring: false,
      bookingWindows: {} as BookingWindowConfiguration,
      tierAccess: {} as TierAccessConfiguration,
      image: '',
    };
  });

  // Collapsible section states
  const [bookingConfigExpanded, setBookingConfigExpanded] = useState(false);
  const [tierAccessExpanded, setTierAccessExpanded] = useState(false);
  const [recurringExpanded, setRecurringExpanded] = useState(false);

  // Slot validation state
  const [slotValidationWarning, setSlotValidationWarning] = useState<string | null>(null);
  const [venueSchedules, setVenueSchedules] = useState<Map<string, any[]>>(new Map());
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  const [loadingVenueSchedules, setLoadingVenueSchedules] = useState<boolean>(false);

  // Booking window configuration state
  const [bookingWindowCards, setBookingWindowCards] = useState<Array<{
    id: string;
    membershipTier: string;
    unlimited: boolean;
    days: number;
  }>>([]);

  // Tier access configuration state
  const [tierAccessCards, setTierAccessCards] = useState<Array<{
    id: string;
    membershipTier: string;
    accessLevel: TierAccessLevel;
  }>>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchAmenities(),
          fetchCoaches(),
          fetchMembershipTiers(),
          fetchTenantSettings(),
          fetchAvailableTypes(),
        ]);

        if (isEditing && groupPlayId) {
          await fetchGroupPlay(groupPlayId);
        } else if (isCopying && copyFromId) {
          await fetchGroupPlayToCopy(copyFromId);
        } else {
          initializeNewGroupPlay();
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupPlayId, copyFromId]);

  const fetchAmenities = async () => {
    const response = await amenityAPI.getAmenities();
    setAmenities(response.data);
  };

  const fetchCoaches = async () => {
    try {
      const response = await userAPI.getCoaches();
      setCoaches(response.data);
    } catch (err) {
      console.error('Error fetching coaches:', err);
      setCoaches([]);
    }
  };

  const fetchMembershipTiers = async () => {
    const response = await membershipTierAPI.getMembershipTiers(true);
    setMembershipTiers(response.data.filter((tier: MembershipTier) => tier.active));
  };

  const fetchTenantSettings = async () => {
    const response = await tenantSettingsAPI.getAllSettings();
    setTenantSettings(response.data);
  };

  const fetchAvailableTypes = async () => {
    try {
      const response = await groupPlayAPI.getGroupPlayTypes();
      setAvailableTypes(response.data);
    } catch (err) {
      console.error('Error fetching group play types:', err);
    }
  };

  const fetchGroupPlay = async (id: string) => {
    const response = await groupPlayAPI.getGroupPlayById(id);
    const gp = response.data;
    setGroupPlayData(gp);
    populateFormFromGroupPlay(gp, false);
  };

  const fetchGroupPlayToCopy = async (id: string) => {
    const response = await groupPlayAPI.getGroupPlayById(id);
    const gp = response.data;
    populateFormFromGroupPlay(gp, true);
  };

  const parseTimeForEdit = (timeString: string): Date => {
    if (!timeString) return new Date();
    const timeOnly = timeString.split('+')[0].split('-')[0];
    const [hours, minutes] = timeOnly.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const populateFormFromGroupPlay = (gp: GroupPlay, isCopy: boolean) => {
    const startTime = parseTimeForEdit(gp.startTime);
    const endTime = parseTimeForEdit(gp.endTime);
    const scheduleDate = parseDateOnlyString(gp.scheduleDate);

    setFormData({
      name: isCopy ? `${gp.name} - Copy` : gp.name,
      type: gp.type || '',
      description: gp.description || '',
      coachId: gp.coachId || '',
      isGuestCoach: gp.isGuestCoach || false,
      guestCoachName: gp.guestCoachName || '',
      venues: gp.venues || [],
      scheduleDate,
      startTime,
      endTime,
      durationMinutes: gp.durationMinutes,
      capacity: gp.capacity,
      isWaitlistAvailable: gp.isWaitlistAvailable,
      allowGuestBookings: gp.allowGuestBookings,
      memberSessionFee: gp.memberSessionFee,
      guestSessionFee: gp.guestSessionFee || 0,
      active: gp.active,
      notes: gp.notes || '',
      recurringPattern: gp.recurringPattern || null,
      isRecurring: !!gp.recurringPattern,
      bookingWindows: gp.bookingWindows || {},
      tierAccess: gp.tierAccess || {},
      image: gp.image || '',
    });

    // Initialize booking window cards
    const existingWindows = gp.bookingWindows || {};
    const windowCards = Object.entries(existingWindows).map(([tierName, window], index) => ({
      id: `card-${Date.now()}-${index}`,
      membershipTier: tierName,
      unlimited: (window as any).unlimited || false,
      days: (window as any).advanceBookingDays || 7,
    }));
    setBookingWindowCards(windowCards);
    setBookingConfigExpanded(Object.keys(existingWindows).length > 0);

    // Initialize tier access cards
    const existingAccess = gp.tierAccess || {};
    const accessCards = Object.entries(existingAccess).map(([tierName, accessLevel], index) => ({
      id: `tier-${Date.now()}-${index}`,
      membershipTier: tierName,
      accessLevel: accessLevel as TierAccessLevel,
    }));
    setTierAccessCards(accessCards);
    setTierAccessExpanded(Object.keys(existingAccess).length > 0);
    setRecurringExpanded(!!gp.recurringPattern);
  };

  const initializeNewGroupPlay = () => {
    const defaultBookingWindows = tenantSettings?.default_booking_windows || {};
    const initialTierAccess = membershipTiers.reduce((acc, tier) => {
      acc[tier.name] = TierAccessLevel.FREE;
      return acc;
    }, {} as TierAccessConfiguration);

    setFormData(prev => ({
      ...prev,
      bookingWindows: defaultBookingWindows,
      tierAccess: initialTierAccess,
    }));

    // Initialize booking window cards
    const windowCards = Object.entries(defaultBookingWindows).map(([tierName, window], index) => ({
      id: `card-${Date.now()}-${index}`,
      membershipTier: tierName,
      unlimited: (window as any).unlimited || false,
      days: (window as any).advanceBookingDays || 7,
    }));
    setBookingWindowCards(windowCards);

    // Initialize tier access cards
    const accessCards = membershipTiers.map((tier, index) => ({
      id: `tier-${Date.now()}-${index}`,
      membershipTier: tier.name,
      accessLevel: TierAccessLevel.FREE,
    }));
    setTierAccessCards(accessCards);
  };

  // Booking window management
  const addBookingWindowCard = () => {
    setBookingWindowCards([...bookingWindowCards, {
      id: `card-${Date.now()}`,
      membershipTier: '',
      unlimited: false,
      days: 7,
    }]);
  };

  const removeBookingWindowCard = (cardId: string) => {
    const card = bookingWindowCards.find(c => c.id === cardId);
    setBookingWindowCards(bookingWindowCards.filter(c => c.id !== cardId));
    if (card?.membershipTier) {
      const { [card.membershipTier]: removed, ...rest } = formData.bookingWindows;
      setFormData({ ...formData, bookingWindows: rest });
    }
  };

  const updateBookingWindowCard = (cardId: string, field: string, value: any) => {
    const updated = bookingWindowCards.map(card => {
      if (card.id === cardId) {
        const oldMembershipTier = card.membershipTier;
        const newCard = { ...card, [field]: value };
        
        // Update formData.bookingWindows
        if (field === 'membershipTier') {
          // Remove old tier and add new tier
          setFormData(prev => {
            const { [oldMembershipTier]: removed, ...rest } = prev.bookingWindows;
            return {
              ...prev,
              bookingWindows: newCard.membershipTier ? {
                ...rest,
                [newCard.membershipTier]: {
                  unlimited: newCard.unlimited,
                  advanceBookingDays: newCard.days,
                },
              } : rest,
            };
          });
        } else if (newCard.membershipTier && (field === 'unlimited' || field === 'days')) {
          // Update properties for existing tier
          setFormData(prev => ({
            ...prev,
            bookingWindows: {
              ...prev.bookingWindows,
              [newCard.membershipTier]: {
                unlimited: newCard.unlimited,
                advanceBookingDays: newCard.days,
              },
            },
          }));
        }
        return newCard;
      }
      return card;
    });
    setBookingWindowCards(updated);
  };

  // Tier access management
  const addTierAccessCard = () => {
    setTierAccessCards([...tierAccessCards, {
      id: `tier-${Date.now()}`,
      membershipTier: '',
      accessLevel: TierAccessLevel.FREE,
    }]);
  };

  const removeTierAccessCard = (cardId: string) => {
    const card = tierAccessCards.find(c => c.id === cardId);
    setTierAccessCards(tierAccessCards.filter(c => c.id !== cardId));
    if (card?.membershipTier) {
      const { [card.membershipTier]: removed, ...rest } = formData.tierAccess;
      setFormData({ ...formData, tierAccess: rest });
    }
  };

  const updateTierAccessCard = (cardId: string, field: string, value: any) => {
    const updated = tierAccessCards.map(card => {
      if (card.id === cardId) {
        const oldMembershipTier = card.membershipTier;
        const newCard = { ...card, [field]: value };
        
        // Update formData.tierAccess whenever membershipTier or accessLevel changes
        if (field === 'membershipTier') {
          // Remove old tier and add new tier
          setFormData(prev => {
            const { [oldMembershipTier]: removed, ...rest } = prev.tierAccess;
            return {
              ...prev,
              tierAccess: newCard.membershipTier ? {
                ...rest,
                [newCard.membershipTier]: newCard.accessLevel,
              } : rest,
            };
          });
        } else if (field === 'accessLevel' && newCard.membershipTier) {
          // Update access level for existing tier
          setFormData(prev => ({
            ...prev,
            tierAccess: {
              ...prev.tierAccess,
              [newCard.membershipTier]: newCard.accessLevel,
            },
          }));
        }
        return newCard;
      }
      return card;
    });
    setTierAccessCards(updated);
  };

  // Day of week checkbox handler for recurring pattern
  const handleDayOfWeekChange = (day: number, checked: boolean) => {
    const currentDays = formData.recurringPattern?.daysOfWeek || [];
    const scheduleDateDayOfWeek = formData.scheduleDate.getDay();

    let newDays;
    if (checked) {
      newDays = [...currentDays, day].sort();
    } else {
      if (day === scheduleDateDayOfWeek) {
        setError('Cannot uncheck the schedule date\'s day of week');
        return;
      }
      newDays = currentDays.filter(d => d !== day);
    }

    setFormData({
      ...formData,
      recurringPattern: {
        ...formData.recurringPattern!,
        daysOfWeek: newDays,
      },
    });
  };

  // Fetch schedules for selected venues
  const fetchVenueSchedules = async (venueIds: string[]) => {
    if (venueIds.length === 0) {
      setVenueSchedules(new Map());
      setVenueNames(new Map());
      return;
    }

    try {
      setLoadingVenueSchedules(true);
      const scheduleMap = new Map<string, any[]>();
      const nameMap = new Map<string, string>();

      for (const venueId of venueIds) {
        try {
          const response = await amenityScheduleAPI.getAmenitySchedules(venueId);
          scheduleMap.set(venueId, response.data);
          
          const amenity = amenities.find(a => a.id === venueId);
          if (amenity) {
            nameMap.set(venueId, amenity.name);
          }
        } catch (err) {
          console.error(`Error fetching schedule for venue ${venueId}:`, err);
        }
      }

      setVenueSchedules(scheduleMap);
      setVenueNames(nameMap);
    } catch (error) {
      console.error('Error fetching venue schedules:', error);
    } finally {
      setLoadingVenueSchedules(false);
    }
  };

  // Validate slot alignment when venues or times change
  const validateSlotAlignment = async () => {
    if (formData.venues.length === 0) {
      setSlotValidationWarning(null);
      return;
    }

    const startTimeStr = dateToTimeString(formData.startTime);
    const endTimeStr = dateToTimeString(formData.endTime);

    // Use scheduleDate as representative date for validation
    const result = await validateCoachingTimeSlotAlignment(
      formData.venues,
      venueSchedules,
      venueNames,
      formData.scheduleDate,
      startTimeStr,
      endTimeStr
    );

    if (!result.valid) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else if (result.warnings.length > 0) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else {
      setSlotValidationWarning(null);
    }
  };

  // Effect to fetch venue schedules when venues change
  useEffect(() => {
    if (formData.venues.length > 0 && amenities.length > 0) {
      fetchVenueSchedules(formData.venues);
    } else {
      setVenueSchedules(new Map());
      setVenueNames(new Map());
      setSlotValidationWarning(null);
    }
  }, [formData.venues, amenities]);

  // Effect to validate slot alignment when schedules are loaded or times change
  useEffect(() => {
    if (venueSchedules.size > 0 && formData.venues.length > 0) {
      validateSlotAlignment();
    }
  }, [venueSchedules, formData.startTime, formData.endTime, formData.durationMinutes, formData.scheduleDate]);

  // Effect to auto-update days of week when schedule date changes and recurring is enabled
  useEffect(() => {
    if (formData.isRecurring && formData.recurringPattern) {
      const scheduleDateDayOfWeek = formData.scheduleDate.getDay();
      const currentDays = formData.recurringPattern.daysOfWeek || [];
      
      // Only update if the schedule date's day is not already in the array
      if (!currentDays.includes(scheduleDateDayOfWeek)) {
        setFormData({
          ...formData,
          recurringPattern: {
            ...formData.recurringPattern,
            daysOfWeek: [...currentDays, scheduleDateDayOfWeek].sort(),
          },
        });
      }
    }
  }, [formData.scheduleDate, formData.isRecurring]);

  const calculateDuration = (start: Date, end: Date): number => {
    const startMin = start.getHours() * 60 + start.getMinutes();
    const endMin = end.getHours() * 60 + end.getMinutes();
    let duration = endMin - startMin;
    if (duration < 0) duration += 24 * 60;
    return Math.max(duration, 0);
  };

  const handleStartTimeChange = (newStartTime: Date | null) => {
    if (!newStartTime) return;
    const newEndTime = new Date(newStartTime);
    newEndTime.setMinutes(newStartTime.getMinutes() + formData.durationMinutes);
    setFormData({ ...formData, startTime: newStartTime, endTime: newEndTime });
  };

  const handleDelete = async () => {
    if (!groupPlayId) return;

    try {
      await groupPlayAPI.deleteGroupPlay(groupPlayId);
      setSuccessMessage('Group play deleted successfully');
      setTimeout(() => {
        navigate('/group-plays');
      }, 1500);
      setOpenDeleteDialog(false);
    } catch (error: any) {
      console.error('Error deleting group play:', error);
      setError(error.response?.data?.message || 'Failed to delete group play');
      setOpenDeleteDialog(false);
    }
  };

  const handleReservationDialogClose = () => {
    setReservationDialogOpen(false);
    setReservationDialogData(null);
    // Navigate after closing reservation dialog
    navigate('/group-plays');
  };

  const handleEndDateChange = async (date: Date | null) => {
    if (!isEditing || !groupPlayId || !formData.recurringPattern) {
      // For new group plays or non-recurring, just update the state
      setFormData({
        ...formData,
        recurringPattern: {
          ...formData.recurringPattern!,
          endDate: date ? formatDateForInput(date) : undefined,
        },
      });
      return;
    }

    // For existing recurring group plays, check if setting a new end date
    const currentEndDate = formData.recurringPattern.endDate;
    
    if (date && (!currentEndDate || formatDateForInput(date) !== currentEndDate)) {
      // Setting a new end date - check for affected instances
      try {
        setIsCheckingAffectedInstances(true);
        const response = await groupPlayAPI.checkAffectedInstancesByEndDate(
          groupPlayId,
          formatDateForInput(date)
        );
        
        if (response.data.affectedInstances && response.data.affectedInstances.length > 0) {
          // Show confirmation dialog
          setAffectedInstancesData({
            instances: response.data.affectedInstances,
            totalAffected: response.data.totalAffected,
            totalBookings: response.data.totalBookings,
            newEndDate: formatDateForInput(date)
          });
          setPendingEndDate(date);
          setAffectedInstancesDialogOpen(true);
        } else {
          // No affected instances, just update
          setFormData({
            ...formData,
            recurringPattern: {
              ...formData.recurringPattern!,
              endDate: formatDateForInput(date),
            },
          });
        }
      } catch (error: any) {
        console.error('Error checking affected instances:', error);
        setError('Failed to check affected instances');
      } finally {
        setIsCheckingAffectedInstances(false);
      }
    } else {
      // Just update the date normally
      setFormData({
        ...formData,
        recurringPattern: {
          ...formData.recurringPattern!,
          endDate: date ? formatDateForInput(date) : undefined,
        },
      });
    }
  };

  const handleConfirmEndDateChange = () => {
    if (pendingEndDate) {
      setFormData({
        ...formData,
        recurringPattern: {
          ...formData.recurringPattern!,
          endDate: formatDateForInput(pendingEndDate),
        },
      });
    }
    setAffectedInstancesDialogOpen(false);
    setAffectedInstancesData(null);
    setPendingEndDate(null);
  };

  const handleCancelEndDateChange = () => {
    setAffectedInstancesDialogOpen(false);
    setAffectedInstancesData(null);
    setPendingEndDate(null);
  };

  const handleClearEndDate = () => {
    // Clearing end date means creating instances for the next 3 months
    setFormData({
      ...formData,
      recurringPattern: {
        ...formData.recurringPattern!,
        endDate: undefined,
      },
    });
  };

  const handleDurationChange = (durationMinutes: number) => {
    const newEndTime = new Date(formData.startTime);
    newEndTime.setMinutes(formData.startTime.getMinutes() + durationMinutes);
    setFormData({ ...formData, durationMinutes, endTime: newEndTime });
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Group play name is required');
      return;
    }
    if (formData.venues.length === 0) {
      setError('At least one venue is required');
      return;
    }
    if (formData.capacity < 1) {
      setError('Capacity must be at least 1');
      return;
    }
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      return;
    }

    // Check slot validation warnings
    if (slotValidationWarning) {
      setError('Please resolve venue slot alignment issues before saving');
      return;
    }

    try {
      setSubmitting(true);

      // Format times as HH:mm (24-hour format, no seconds or timezone)
      const formatTime = (date: Date): string => {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      };

      const startTime = formatTime(formData.startTime);
      const endTime = formatTime(formData.endTime);

      // Build payload with all fields - backend will detect actual changes
      const payload: any = {
        name: formData.name,
        type: formData.type || null,
        description: formData.description,
        coachId: formData.isGuestCoach ? null : (formData.coachId || null),
        isGuestCoach: formData.isGuestCoach,
        guestCoachName: formData.isGuestCoach ? formData.guestCoachName : null,
        capacity: formData.capacity,
        isWaitlistAvailable: formData.isWaitlistAvailable,
        allowGuestBookings: formData.allowGuestBookings,
        memberSessionFee: parseFloat(formData.memberSessionFee.toString()),
        guestSessionFee: formData.allowGuestBookings ? parseFloat(formData.guestSessionFee.toString()) : null,
        bookingWindows: formData.bookingWindows,
        tierAccess: formData.tierAccess,
        active: formData.active,
        notes: formData.notes,
        recurringPattern: formData.isRecurring ? formData.recurringPattern : null,
        image: formData.image,
        // Always include scheduling fields - backend detects if they actually changed
        scheduleDate: formatDateForInput(formData.scheduleDate),
        startTime: startTime,
        endTime: endTime,
        durationMinutes: formData.durationMinutes,
        venues: formData.venues,
      };

      if (isEditing && groupPlayId) {
        const response = await groupPlayAPI.updateGroupPlay(groupPlayId, payload);
        // Show reservation results if available
        if (response.data.reservationResult) {
          setReservationDialogData({
            entityName: formData.name,
            operationType: 'update',
            reservationResult: response.data.reservationResult
          });
          setReservationDialogOpen(true);
        } else {
          setSuccessMessage('Group play updated successfully');
          setTimeout(() => navigate('/group-plays'), 1500);
        }
      } else {
        const response = await groupPlayAPI.createGroupPlay(payload);
        // Show reservation results if available
        if (response.data.reservationResult) {
          setReservationDialogData({
            entityName: formData.name,
            operationType: 'create',
            reservationResult: response.data.reservationResult
          });
          setReservationDialogOpen(true);
        } else {
          setSuccessMessage('Group play created successfully');
          setTimeout(() => navigate('/group-plays'), 1500);
        }
      }
    } catch (err: any) {
      console.error('Error saving group play:', err);
      setError(err.response?.data?.message || 'Failed to save group play');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <AdminAccessControl allowedRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]}>
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs>
              <Link component="button" onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center' }}>
                <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                Home
              </Link>
              <Link component="button" onClick={() => navigate('/group-plays')}>
                Group Plays
              </Link>
              <Typography color="text.primary">
                {isEditing ? 'Edit' : isCopying ? 'Copy' : 'Create'} Group Play
              </Typography>
            </Breadcrumbs>
          </Box>

          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h4">
                {isEditing ? 'Edit' : isCopying ? 'Copy' : 'Create'} Group Play
              </Typography>
              <Box>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setOpenDeleteDialog(true)}
                    sx={{ mr: 1 }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => navigate('/group-plays')}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save'}
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid size={{xs:12}}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <TextField
                  fullWidth
                  label="Group Play Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <Autocomplete
                  freeSolo
                  options={availableTypes}
                  value={formData.type || ''}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, type: newValue || '' });
                  }}
                  onInputChange={(_, newInputValue) => {
                    setFormData({ ...formData, type: newInputValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type"
                      placeholder="Enter or select a type"
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <TextField
                  fullWidth
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                  required
                />
              </Grid>

              <Grid size={{xs:12}}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={{xs:12, md:6}}>
                <Stack spacing={2}>
                  <Autocomplete
                    options={coaches}
                    getOptionLabel={(coach) => coach.firstName && coach.lastName ? `${coach.firstName} ${coach.lastName}` : coach.email || 'Unknown Coach'}
                    value={coaches.find(coach => coach.id === formData.coachId) || null}
                    onChange={(event, newValue) => {
                      setFormData({ 
                        ...formData, 
                        coachId: newValue?.id || '',
                        guestCoachName: newValue ? (newValue.firstName && newValue.lastName ? `${newValue.firstName} ${newValue.lastName}` : newValue.email || 'Unknown Coach') : formData.guestCoachName
                      });
                    }}
                    disabled={formData.isGuestCoach}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coach (Optional)"
                        helperText="Select a coach or use guest coach below"
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? '' : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.guestCoachName}
                      onChange={(e) => setFormData({ ...formData, guestCoachName: e.target.value })}
                      fullWidth
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              {/* Venues */}
              <Grid size={{xs:12}}>
                <Autocomplete
                  multiple
                  options={amenities}
                  getOptionLabel={(option) => `${option.name} (${option.type})`}
                  getOptionKey={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={amenities.filter(a => formData.venues.includes(a.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, venues: newValue.map(v => v.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Venues"
                      required
                      placeholder="Select one or more venues/courts"
                      helperText="Select amenities to reserve for this group play"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option.name}
                          {...tagProps}
                        />
                      );
                    })
                  }
                />
              </Grid>

              {/* Slot Validation Warning */}
              {(slotValidationWarning || loadingVenueSchedules) && (
                <Grid size={{xs:12}}>
                  {loadingVenueSchedules ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Validating venue schedules...
                      </Typography>
                    </Box>
                  ) : slotValidationWarning ? (
                    <Alert 
                      severity={slotValidationWarning.includes('❌') ? 'error' : slotValidationWarning.includes('⚠️') ? 'warning' : 'success'}
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      <strong>Venue Slot Alignment:</strong>
                      {slotValidationWarning}
                    </Alert>
                  ) : null}
                </Grid>
              )}

              {/* Schedule */}
              <Grid size={{xs:12}}>
                <Typography variant="h6" gutterBottom>
                  Schedule
                </Typography>
              </Grid>

              <Grid size={{xs:12, md:4}}>
                <DatePicker
                  label="Schedule Date"
                  value={formData.scheduleDate}
                  onChange={(date) => date && setFormData({ ...formData, scheduleDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{xs:12, md:3}}>
                <TimePicker
                  label="Start Time"
                  value={formData.startTime}
                  onChange={handleStartTimeChange}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{xs:12, md:3}}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={formData.durationMinutes}
                  onChange={(e) => handleDurationChange(parseInt(e.target.value) || 0)}
                />
              </Grid>

              <Grid size={{xs:12, md:2}}>
                <TimePicker
                  label="End Time"
                  value={formData.endTime}
                  disabled
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              {/* Settings */}
              <Grid size={{xs:12}}>
                <Typography variant="h6" gutterBottom>
                  Settings
                </Typography>
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isWaitlistAvailable}
                      onChange={(e) => setFormData({ ...formData, isWaitlistAvailable: e.target.checked })}
                    />
                  }
                  label="Enable Waitlist"
                />
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowGuestBookings}
                      onChange={(e) => setFormData({ ...formData, allowGuestBookings: e.target.checked })}
                    />
                  }
                  label="Allow Guest Bookings"
                />
              </Grid>

              {/* Pricing */}
              <Grid size={{xs:12}}>
                <Typography variant="h6" gutterBottom>
                  Pricing
                </Typography>
              </Grid>

              <Grid size={{xs:12, md:6}}>
                <TextField
                  fullWidth
                  label="Member Session Fee"
                  type="number"
                  value={formData.memberSessionFee}
                  onChange={(e) => setFormData({ ...formData, memberSessionFee: parseFloat(e.target.value) || 0 })}
                  InputProps={{ startAdornment: '$' }}
                />
              </Grid>

              {formData.allowGuestBookings && (
                <Grid size={{xs:12, md:6}}>
                  <TextField
                    fullWidth
                    label="Guest Session Fee"
                    type="number"
                    value={formData.guestSessionFee}
                    onChange={(e) => setFormData({ ...formData, guestSessionFee: parseFloat(e.target.value) || 0 })}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
              )}

              <Grid size={{xs:12}}>
                <TextField
                  fullWidth
                  label="Notes (Optional)"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>

              {/* Recurring Pattern */}
              <Grid size={{xs:12}}>
                <Accordion expanded={recurringExpanded} onChange={() => setRecurringExpanded(!recurringExpanded)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Recurring Pattern (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid size={{xs:12}}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.isRecurring}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData({
                                  ...formData,
                                  isRecurring: checked,
                                  recurringPattern: checked ? {
                                    frequency: 'weekly',
                                    interval: 1,
                                    daysOfWeek: [formData.scheduleDate.getDay()],
                                    endDate: undefined,
                                  } : null,
                                });
                              }}
                            />
                          }
                          label="Enable Recurring Schedule"
                        />
                      </Grid>

                      {formData.isRecurring && formData.recurringPattern && (
                        <>
                          <Grid size={{xs:12, md:6}}>
                            <FormControl fullWidth>
                              <InputLabel>Frequency</InputLabel>
                              <Select
                                value={formData.recurringPattern.frequency}
                                onChange={(e: SelectChangeEvent) =>
                                  setFormData({
                                    ...formData,
                                    recurringPattern: {
                                      ...formData.recurringPattern!,
                                      frequency: e.target.value as 'daily' | 'weekly' | 'monthly',
                                    },
                                  })}
                                label="Frequency"
                              >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid size={{xs:12, md:6}}>
                            <TextField
                              fullWidth
                              label="Repeat Every"
                              type="number"
                              value={formData.recurringPattern.interval}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  recurringPattern: {
                                    ...formData.recurringPattern!,
                                    interval: parseInt(e.target.value) || 1,
                                  },
                                })}
                            />
                          </Grid>

                          {formData.recurringPattern.frequency === 'weekly' && (
                            <Grid size={{xs:12}}>
                              <Typography variant="body2" gutterBottom>
                                Days of Week:
                              </Typography>
                              <FormGroup row>
                                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                                  <FormControlLabel
                                    key={index}
                                    control={
                                      <Checkbox
                                        checked={formData.recurringPattern?.daysOfWeek?.includes(index) || false}
                                        onChange={(e) => handleDayOfWeekChange(index, e.target.checked)}
                                      />
                                    }
                                    label={day}
                                  />
                                ))}
                              </FormGroup>
                            </Grid>
                          )}

                          <Grid size={{xs:12, md:6}}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <DatePicker
                                label="End Date (Optional)"
                                value={formData.recurringPattern.endDate ? parseDateOnlyString(formData.recurringPattern.endDate) : null}
                                onChange={handleEndDateChange}
                                disabled={isCheckingAffectedInstances}
                                slotProps={{ textField: { fullWidth: true } }}
                              />
                              {formData.recurringPattern.endDate && (
                                <Button
                                  variant="outlined"
                                  onClick={handleClearEndDate}
                                  sx={{ mt: 1, minWidth: 'auto', px: 2 }}
                                  disabled={isCheckingAffectedInstances}
                                >
                                  Clear
                                </Button>
                              )}
                            </Box>
                            {!formData.recurringPattern.endDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Instances will be created for the next 3 months
                              </Typography>
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Booking Windows */}
              <Grid size={{xs:12}}> 
                <Accordion expanded={bookingConfigExpanded} onChange={() => setBookingConfigExpanded(!bookingConfigExpanded)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Booking Windows Configuration (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addBookingWindowCard}
                        size="small"
                        sx={{ mb: 2 }}
                      >
                        Add Booking Window
                      </Button>

                      {bookingWindowCards.map((card) => (
                        <Card key={card.id} sx={{ mb: 2 }}>
                          <CardContent>
                            <Grid container spacing={2} alignItems="center">
                              <Grid size={{xs:12, md:4}}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Membership Tier</InputLabel>
                                  <Select
                                    value={card.membershipTier}
                                    onChange={(e: SelectChangeEvent) => updateBookingWindowCard(card.id, 'membershipTier', e.target.value)}
                                    label="Membership Tier"
                                  >
                                    {membershipTiers.map((tier) => (
                                      <MenuItem key={tier.id} value={tier.name}>
                                        {tier.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={{xs:12, md:3}}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={card.unlimited}
                                      onChange={(e) => updateBookingWindowCard(card.id, 'unlimited', e.target.checked)}
                                    />
                                  }
                                  label="Unlimited"
                                />
                              </Grid>
                              {!card.unlimited && (
                                <Grid size={{xs:12, md:3}} >
                                  <TextField
                                    fullWidth
                                    label="Days Ahead"
                                    type="number"
                                    size="small"
                                    value={card.days}
                                    onChange={(e) => updateBookingWindowCard(card.id, 'days', parseInt(e.target.value) || 7)}
                                  />
                                </Grid>
                              )}
                              <Grid size={{xs:12, md:2}} >
                                <IconButton
                                  color="error"
                                  onClick={() => removeBookingWindowCard(card.id)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Tier Access */}
              <Grid size={{xs:12}}>
                <Accordion expanded={tierAccessExpanded} onChange={() => setTierAccessExpanded(!tierAccessExpanded)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Tier Access Configuration</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addTierAccessCard}
                        size="small"
                        sx={{ mb: 2 }}
                      >
                        Add Tier Access
                      </Button>

                      {tierAccessCards.map((card) => (
                        <Card key={card.id} sx={{ mb: 2 }}>
                          <CardContent>
                            <Grid container spacing={2} alignItems="center">
                              <Grid size={{xs:12, md:5}}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Membership Tier</InputLabel>
                                  <Select
                                    value={card.membershipTier}
                                    onChange={(e: SelectChangeEvent) => updateTierAccessCard(card.id, 'membershipTier', e.target.value)}
                                    label="Membership Tier"
                                  >
                                    {membershipTiers.map((tier) => (
                                      <MenuItem key={tier.id} value={tier.name}>
                                        {tier.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={{xs:12, md:5}}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Access Level</InputLabel>
                                  <Select
                                    value={card.accessLevel}
                                    onChange={(e: SelectChangeEvent) => updateTierAccessCard(card.id, 'accessLevel', e.target.value)}
                                    label="Access Level"
                                  >
                                    <MenuItem value={TierAccessLevel.FREE}>Free</MenuItem>
                                    <MenuItem value={TierAccessLevel.PAID}>Paid</MenuItem>
                                    <MenuItem value={TierAccessLevel.NO_ACCESS}>No Access</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={{xs:12, md:2}}>
                                <IconButton
                                  color="error"
                                  onClick={() => removeTierAccessCard(card.id)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Paper>

          <Snackbar
            open={!!successMessage}
            autoHideDuration={6000}
            onClose={() => setSuccessMessage(null)}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Delete Confirmation Dialog */}
          <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete "{formData.name}"?
                {formData.recurringPattern && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="warning">
                      This will delete the group play and all its recurring instances.
                    </Alert>
                  </Box>
                )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={handleDelete} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reservation Results Dialog */}
          {reservationDialogData && (
            <ReservationResultsDialog
              open={reservationDialogOpen}
              onClose={handleReservationDialogClose}
              entityType="session"
              entityName={reservationDialogData.entityName}
              operationType={reservationDialogData.operationType}
              reservationResult={reservationDialogData.reservationResult}
            />
          )}

          {/* Affected Instances Confirmation Dialog */}
          <Dialog 
            open={affectedInstancesDialogOpen} 
            onClose={handleCancelEndDateChange}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Confirm End Date Change</DialogTitle>
            <DialogContent>
              {affectedInstancesData && (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Setting the end date to <strong>{affectedInstancesData.newEndDate}</strong> will cancel{' '}
                    <strong>{affectedInstancesData.totalAffected}</strong> instance(s) scheduled after this date.
                  </Alert>
                  
                  {affectedInstancesData.totalBookings > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <strong>{affectedInstancesData.totalBookings}</strong> booking(s) across these instances will be cancelled.
                    </Alert>
                  )}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    The following instances will be cancelled:
                  </Typography>
                  
                  <Box sx={{ maxHeight: 300, overflow: 'auto', mt: 1 }}>
                    {affectedInstancesData.instances.map((instance: any, index: number) => (
                      <Card key={instance.id} sx={{ mb: 1 }}>
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="body2">
                            <strong>{formatDate(instance.sessionDate)}</strong> at{' '}
                            {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                            {instance.bookingCount > 0 && (
                              <Chip 
                                label={`${instance.bookingCount} booking(s)`} 
                                size="small" 
                                color="error" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Do you want to proceed with this change?
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelEndDateChange}>Cancel</Button>
              <Button onClick={handleConfirmEndDateChange} color="error" variant="contained">
                Confirm & Cancel Instances
              </Button>
            </DialogActions>
          </Dialog>
        </AdminAccessControl>
      </Container>
    </LocalizationProvider>
  );
};

export default CreateEditGroupPlayPage;
